import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/utils/api';
import { FuelPicker, SignatureField, sigData, PhoneQrPanel } from './ReceptionFields';

const List = ({ title, items, testid }) => items.length > 0 && (
  <div><p className="text-xs font-bold uppercase text-slate-500">{title}</p>
    <ul className="list-disc ml-4 text-sm" data-testid={testid}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
);

export const WorkSummary = ({ s }) => (
  <div className="p-3 rounded-xl bg-slate-50 border space-y-2" data-testid="checkout-summary">
    <div><p className="font-semibold">{s.client_name}</p>{s.vehicle_info && <p className="text-sm text-slate-600">{s.vehicle_info}</p>}</div>
    <p className="text-sm font-bold flex items-center gap-1"><Wrench className="w-4 h-4" />Travaux effectués</p>
    <List title="Demande" items={s.motifs} testid="checkout-motifs" />
    <List title="Réalisé" items={s.operations} testid="checkout-operations" />
    <List title="Pièces" items={s.parts} testid="checkout-parts" />
    {s.recommendations && <div><p className="text-xs font-bold uppercase text-slate-500">Recommandations</p><p className="text-sm whitespace-pre-line" data-testid="checkout-reco">{s.recommendations}</p></div>}
    <p className="text-xs text-slate-500" data-testid="checkout-checkin-ref">À la réception : {s.checkin.mileage ? `${Number(s.checkin.mileage).toLocaleString('fr-CH')} km` : '— km'} · carburant {s.checkin.fuel_level || '—'}</p>
  </div>
);

export const CheckOutForm = ({ s, onDone }) => {
  const [f, setF] = useState({ mileage: s.checkin.mileage ? String(s.checkin.mileage) : '', fuel_level: '', remarks: '', signer_name: s.client_name || '' });
  const [saving, setSaving] = useState(false);
  const sig = useRef(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const submit = async () => {
    const signature = sigData(sig);
    if (!signature) { toast.error('Faites signer le client'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/agenda/appointments/${s.id}/check-out`, { ...f, mileage: parseInt(f.mileage, 10) || 0, signature });
      toast.success('Véhicule restitué au client'); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="checkout-form">
      <WorkSummary s={s} />
      <div><Label>Kilométrage au retour</Label><Input type="number" inputMode="numeric" value={f.mileage} onChange={e => set('mileage', e.target.value)} placeholder="km" data-testid="checkout-mileage-input" /></div>
      <div><Label>Niveau de carburant au retour</Label><FuelPicker value={f.fuel_level} onChange={v => set('fuel_level', v)} prefix="checkout" /></div>
      <div><Label>Remarques</Label><Textarea rows={2} value={f.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Ex : client informé du prochain service" data-testid="checkout-remarks-input" /></div>
      <SignatureField sigRef={sig} name={f.signer_name} onName={v => set('signer_name', v)} prefix="checkout" label="Signature du client (je récupère mon véhicule)" />
      <Button className="w-full h-12 text-base bg-[#0E1116] hover:bg-black text-[#FACC15]" onClick={submit} disabled={saving} data-testid="checkout-submit-btn">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Valider la restitution'}
      </Button>
    </div>
  );
};

export const CheckOutDialog = ({ appt, onClose, onDone }) => {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/agenda/appointments/${appt.id}/checkout-summary`).then(r => setS(r.data)).catch(() => toast.error('Fiche introuvable')); }, [appt.id]);
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader><DialogTitle>Restitution du véhicule</DialogTitle></DialogHeader>
        <PhoneQrPanel apptId={appt.id} kind="checkout" path="/restitution-mobile" title="Faire signer avec le téléphone" doneMsg="Restitution signée sur le téléphone" onDone={onDone}
          steps={['Scannez ce QR code avec le téléphone', 'Montrez le résumé des travaux au client', 'Km, carburant, puis signature du client']} />
        <p className="text-xs text-center text-slate-500 uppercase tracking-wide">ou directement ici</p>
        {s ? <CheckOutForm s={s} onDone={onDone} /> : <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
      </DialogContent>
    </Dialog>
  );
};
